import { useState, useEffect, useCallback, LegacyRef, forwardRef, PropsWithChildren, useMemo } from "react";
import { MultiSelectTree, MultiSelectTreeChangeEvent, MultiSelectTreeExpandEvent, getMultiSelectTreeValue, MultiSelectTreeFilterChangeEvent, DropDownListHandle } from "@progress/kendo-react-dropdowns";
import { processMultiSelectTreeData, expandedState } from "./utils";
import { FilterDescriptor } from "@progress/kendo-data-query";
import { FloatingLabel } from "react-bootstrap";
import { ZIndexContext } from "@progress/kendo-react-common";
import { MultiSelectDropdownOptions } from "./MultiSelectDropdownOption";

const dataItemKey = "value";
const textField = "text";
const checkField = "checkField";
const checkIndeterminateField = "checkIndeterminateField";
const subItemsField = "items";
const expandField = "expanded";

const fields = {
  dataItemKey,
  checkField,
  checkIndeterminateField,
  expandField,
  subItemsField,
};

interface MultiSelectDropdownProps extends PropsWithChildren {
  label: string;
  name: string;
  className?: string;
  filterable: boolean;
  options: MultiSelectDropdownOptions[];
  onChange?: (e: MultiSelectDropdownOptions[]) => void;
  value: any[];
}

export const MultiSelectDropdown = forwardRef(
  (
    props: Readonly<MultiSelectDropdownProps>,
    ref: LegacyRef<DropDownListHandle> | undefined
  ) => {
    const [value, setValue] = useState<any[]>([]);
    const [expanded, setExpanded] = useState<string[]>([]);
    const [filter, setFilter] = useState<FilterDescriptor | null>(null);
    const [data, setData] = useState<MultiSelectDropdownOptions[]>([]);
    const [count, setCount] = useState<number>(0);

    const onChange = (event: MultiSelectTreeChangeEvent) => {
      const selectedValues = getMultiSelectTreeValue(data, {
        ...fields,
        ...event,
        value,
      });
      const allSelected = selectedValues.some((value) => value.value === "");
      allSelected
        ? setCount(selectedValues.length - 1)
        : setCount(selectedValues.length);
      props.onChange && props.onChange(selectedValues);
      setValue(selectedValues);
    };

    const onExpandChange = useCallback(
      (event: MultiSelectTreeExpandEvent) =>
        setExpanded(expandedState(event.item, dataItemKey, expanded)),
      [expanded]
    );

    const treeData = useMemo(
      () =>
        processMultiSelectTreeData(data, {
          expanded,
          value,
          filter,
          ...fields,
        }),
      [data, expanded, value, filter]
    );

    const onFilterChange = (event: MultiSelectTreeFilterChangeEvent) =>
      setFilter(event.filter);

    const renderValue = () => {
      return count === 0 ? "Please select..." : `${count} options selected`;
    };

    useEffect(() => {
      setData(props.options);

      if (props.options && props.options.length > 0) {
        const initialExpanded = props.options
          .filter((option) => option.items && option.items.length > 0)
          .map((option) => option.value);

        setExpanded(initialExpanded);
        
        const allSelected = props.value.some((value) => value.value === "");
        allSelected
          ? setCount(props.value.length - 1)
          : setCount(props.value.length);
        setValue(props.value);
      } else {
        setExpanded([]);
        setCount(0);
        setValue([]);
      }
    }, [props.options, props.value]);

    return (
      <FloatingLabel
        className="mb-3 h-100 floating-label-fixed"
        label={`${props.label}`}
        style={{ color: "rgba(var(--bs-body-color-rgb), 0.65)" }}
      >
        <ZIndexContext.Provider value={10000}>
          <div>
            <MultiSelectTree
              className={`bg-transparent ${props.className}`}
              data={treeData}
              value={[]}
              onChange={onChange}
              placeholder={renderValue()}
              textField={textField}
              dataItemKey={dataItemKey}
              checkField={checkField}
              checkIndeterminateField={checkIndeterminateField}
              expandField={expandField}
              subItemsField={subItemsField}
              onExpandChange={onExpandChange}
              filterable={props.filterable}
              onFilterChange={onFilterChange}
            />
          </div>
        </ZIndexContext.Provider>
      </FloatingLabel>
    );
  }
);

export default MultiSelectDropdown;